(function () {
    'use strict';

    // --- Easing ---
    /**
     * @param {number} x1
     * @param {number} y1
     * @param {number} x2
     * @param {number} y2
     * @returns {function(number): number}
     */
    function cubicBezier(x1, y1, x2, y2) {
        return function (t) {
            const cx = 3 * x1, bx = 3 * (x2 - x1) - cx, ax = 1 - cx - bx;
            const cy = 3 * y1, by = 3 * (y2 - y1) - cy, ay = 1 - cy - by;

            let x = t;
            for (let i = 0; i < 8; i++) {
                const xSample = ((ax * x + bx) * x + cx) * x - t;
                const dSample = (3 * ax * x + 2 * bx) * x + cx;
                if (Math.abs(xSample) < 1e-7) break;
                x -= xSample / dSample;
            }

            return ((ay * x + by) * x + cy) * x;
        };
    }

    // --- Wheel Controls Plugin ---
    /** @param {KeenSliderInstance} slider */
    function WheelControls(slider) {
        let cooldown = false;
        const COOLDOWN_MS = 700;
        const DELTA_THRESHOLD = 50; // ignore momentum ghost events below this

        /** @param {WheelEvent} e */
        function onWheel(e) {
            if (Math.abs(e.deltaX) < Math.abs(e.deltaY)) return;
            e.preventDefault();
            if (cooldown) return;

            const delta = e.deltaX !== 0 ? e.deltaX : e.deltaY;
            if (Math.abs(delta) < DELTA_THRESHOLD) return; // kill momentum

            if (delta > 0) slider.next();
            else if (delta < 0) slider.prev();

            cooldown = true;
            setTimeout(() => { cooldown = false; }, COOLDOWN_MS);
        }

        slider.on('created', function () {
            slider.container.addEventListener('wheel', onWheel, { passive: false });
        });

        slider.on('destroyed', function () {
            slider.container.removeEventListener('wheel', onWheel);
        });
    }

    // Tracks the previous active slide to avoid O(n) DOM writes
    /** @type {HTMLElement | null} */
    let prevActiveSlide = null;

    /** @param {KeenSliderInstance} s */
    function updateActiveSlide(s) {
        const rel = s.track.details.rel;
        if (prevActiveSlide) prevActiveSlide.classList.remove('keen-slider__slide--active');
        prevActiveSlide = s.slides[rel] ?? null;
        prevActiveSlide?.classList.add('keen-slider__slide--active');
    }

    /**
     * Destroy all keen-slider instances within a root element.
     * Cleans up event listeners, pagination, autoplay, cloned slides
     * and stale references.
     * @param {Document | Element} [root]
     */
    function destroyKeenSliders(root = document) {
        root.querySelectorAll('[data-keen-slider]').forEach(function (el) {
            const htmlEl = /** @type {HTMLElement} */ (el);
            if (!htmlEl._keenSlider) return;

            try { htmlEl._keenSlider.destroy(); } catch (_) { /* already destroyed */ }
            delete htmlEl._keenSlider;

            // Remove slides that were cloned by the 2-slide infinite workaround
            // so the morph starts from a clean server-rendered structure.
            el.querySelectorAll('.keen-slider__slide[data-cloned]').forEach(function (c) { c.remove(); });
        });
        // Reset the module-level reference so it doesn't point to a detached node
        prevActiveSlide = null;
    }

    /** @param {Document | Element} [root] */
    function initKeenSliders(root = document) {
        root.querySelectorAll('[data-keen-slider]').forEach(function (el) {
            const htmlEl = /** @type {HTMLElement} */ (el);
            // If a previous instance somehow still exists, destroy it first.
            if (htmlEl._keenSlider) {
                try { htmlEl._keenSlider.destroy(); } catch (_) { /* noop */ }
                delete htmlEl._keenSlider;
                prevActiveSlide = null;
                el.querySelectorAll('.keen-slider__slide[data-cloned]').forEach(function (c) { c.remove(); });
            }

            const infinite = htmlEl.dataset.keenInfinite === 'true';
            const autoplay = htmlEl.dataset.keenAutoplay === 'true';
            const slideCount = parseInt(htmlEl.dataset.keenSlideCount ?? '', 10)
            const autoplayMs = parseInt(htmlEl.dataset.keenAutoplaySpeed ?? '', 10) || 4000;
            const showPagination = htmlEl.dataset.keenPagination !== undefined;
            const showPeek = htmlEl.dataset.keenPeek !== undefined;
            const id = htmlEl.id;

            /** @type {ReturnType<typeof setInterval> | undefined} */
            let autoplayInterval;
            let isPointerInside = false;
            /** @type {HTMLButtonElement[]} */
            let paginationDots = [];

            /** @param {KeenSliderInstance} s */
            function createPagination(s) {
                if (!showPagination) return;
                const container = document.createElement('div');
                container.className = 'keen-slider__pagination';
                const count = s.slides.length;
                for (let i = 0; i < count; i++) {
                    const dot = document.createElement('button');
                    dot.className = 'keen-slider__pagination-dot';
                    dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
                    dot.setAttribute('type', 'button');
                    dot.addEventListener('click', () => s.moveToIdx(i));
                    container.appendChild(dot);
                    paginationDots.push(dot);
                }
                htmlEl.parentElement?.appendChild(container);
                updatePagination(s);
            }

            /** @param {KeenSliderInstance} s */
            function updatePagination(s) {
                if (!showPagination || paginationDots.length === 0) return;
                const rel = s.track.details.rel;
                paginationDots.forEach((dot, i) => {
                    dot.classList.toggle('keen-slider__pagination-dot--active', i === rel);
                });
            }

            if (infinite && slideCount === 2) {
                const slides = Array.from(el.querySelectorAll('.keen-slider__slide'));
                slides.forEach(function (slide) {
                    const clone = slide.cloneNode(true);
                    /** @type {HTMLElement} */ (clone).setAttribute('data-cloned', '');
                    el.appendChild(clone);
                });
            }

            /** @type {AbortController | undefined} */
            let pointerAC;

            /** @param {KeenSliderInstance} s */
            function startAutoplay(s) {
                if (!autoplay) return;
                clearInterval(autoplayInterval);
                autoplayInterval = setInterval(() => s.next(), autoplayMs);
            }

            function stopAutoplay() {
                if (!autoplay) return;
                clearInterval(autoplayInterval);
            }

            function onVisibilityChange() {
                if (document.hidden) stopAutoplay();
                else if (!isPointerInside) startAutoplay(slider);
            }

            const slider = new KeenSlider(htmlEl, {
                loop: infinite,
                mode: 'free-snap',
                renderMode: 'performance',
                slides: showPeek ? {
                    perView: 1.15,
                    spacing: 0,
                    origin: 'auto',
                } : {
                    perView: 1,
                    spacing: 0,
                    origin: 'auto',
                },
                breakpoints: {
                    '(min-width: 990px)': {
                        slides: {
                            perView: 'auto',
                            spacing: 0,
                            origin: 'center',
                        },
                    },
                },
                defaultAnimation: {
                    duration: 850,
                    easing: cubicBezier(0.32, 0.72, 0, 1),
                },
                created(s) {
                    updateActiveSlide(s);
                    createPagination(s);

                    // if (id) {
                    //     const prev = document.querySelector(`[data-keen-prev="${id}"]`);
                    //     const next = document.querySelector(`[data-keen-next="${id}"]`);
                    //     if (prev) prev.addEventListener('click', () => s.prev());
                    //     if (next) next.addEventListener('click', () => s.next());
                    // }

                    if (autoplay) {
                        startAutoplay(s);

                        pointerAC = new AbortController();
                        const pointerSignal = pointerAC.signal;

                        htmlEl.addEventListener('pointerenter', () => {
                            isPointerInside = true;
                            stopAutoplay();
                        }, { passive: true, signal: pointerSignal });

                        htmlEl.addEventListener('pointerleave', () => {
                            isPointerInside = false;
                            startAutoplay(s);
                        }, { passive: true, signal: pointerSignal });

                        document.addEventListener('visibilitychange', onVisibilityChange);
                    }
                },
                dragStarted() {
                    stopAutoplay();
                },
                dragEnded() {
                    if (autoplay && !isPointerInside) startAutoplay(slider);
                },
                slideChanged(s) {
                    updateActiveSlide(s);
                    updatePagination(s);
                },
                destroyed() {
                    stopAutoplay();
                    pointerAC?.abort();
                    document.removeEventListener('visibilitychange', onVisibilityChange);
                    htmlEl.parentElement?.querySelector('.keen-slider__pagination')?.remove();
                    paginationDots = [];
                },
            }, [WheelControls]);

            htmlEl._keenSlider = slider;
        });
    }

    // Init on DOM ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => initKeenSliders());
    } else {
        initKeenSliders();
    }

    // Scope re-init to the reloaded section on Shopify theme editor
    document.addEventListener('shopify:section:load', (e) => {
        if (e.target instanceof Element) initKeenSliders(e.target);
    });

    // Destroy sliders BEFORE the morph so autoplay/events stop while the DOM
    // is being mutated.  Re-init AFTER the morph from a clean state.
    document.addEventListener('main:before-morph', () => {
        destroyKeenSliders();
    });
    document.addEventListener('main:morphed', () => {
        initKeenSliders();
    });
}());
